// 피드백 '해당 화면에서 보기' 강조 표시 (2026-10-06).
// 주소에 fbhl 파라미터가 있으면 글에 붙은 요소를 박스·번호·메모로 표시하고 첫 요소로 스크롤한다.
// 요소 위치는 계속 다시 재므로, 늦게 그려지거나 다음 단계에서 나타나는 요소도 나타나는 즉시 표시된다 (첫 스크롤은 15초까지만 시도). 앱 최상단에 한 번 마운트 (dev 도구 게이트 안).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HIGHLIGHT_PARAM, HighlightSpec, readHighlightSpec } from './highlight';

const Z = 2147483000;
const COLOR = '#F97316';
const RETRY_MS = 15000;

interface Box {
  idx: number;
  id: string;
  note?: string;
  rect: DOMRect | null;
}

function findEl(id: string): HTMLElement | null {
  try {
    return document.getElementById(id) || (document.querySelector(`[data-testid="${CSS.escape(id)}"]`) as HTMLElement | null);
  } catch {
    return null;
  }
}

export const FeedbackHighlighter: React.FC = () => {
  const [spec, setSpec] = useState<HighlightSpec | null>(null);
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [hidden, setHidden] = useState(false);
  const scrolled = useRef(false);
  const startedAt = useRef(0);
  const lastSearch = useRef('');

  // 주소 감시 (SPA 이동 포함)
  useEffect(() => {
    const check = () => {
      const s = window.location.search;
      if (s === lastSearch.current) return;
      lastSearch.current = s;
      const next = readHighlightSpec(s);
      setSpec(next);
      setHidden(false);
      scrolled.current = false;
      startedAt.current = Date.now();
    };
    check();
    const t = window.setInterval(check, 500);
    return () => window.clearInterval(t);
  }, []);

  // 요소 위치 측정 (스크롤·리사이즈·늦은 렌더 대응)
  useEffect(() => {
    if (!spec || hidden) return;
    const measure = () => {
      const next = spec.targets.map((t, i) => {
        const el = findEl(t.id);
        return { idx: i + 1, id: t.id, note: t.note, rect: el ? el.getBoundingClientRect() : null };
      });
      setBoxes(next);
      if (!scrolled.current) {
        const first = spec.targets.map((t) => findEl(t.id)).find(Boolean);
        if (first) {
          first.scrollIntoView({ block: 'center', behavior: 'smooth' });
          scrolled.current = true;
        } else if (Date.now() - startedAt.current > RETRY_MS) {
          scrolled.current = true; // 더 찾지 않음
        }
      }
    };
    measure();
    const t = window.setInterval(measure, 250);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.clearInterval(t);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [spec, hidden]);

  const close = useCallback(() => {
    setHidden(true);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete(HIGHLIGHT_PARAM);
      window.history.replaceState(window.history.state, '', url.toString());
      lastSearch.current = url.search;
    } catch {
      /* noop */
    }
  }, []);

  const goTo = (id: string) => findEl(id)?.scrollIntoView({ block: 'center', behavior: 'smooth' });

  if (!spec || hidden) return null;
  const missing = boxes.filter((b) => !b.rect);
  const waiting = missing.length > 0 && Date.now() - startedAt.current < RETRY_MS;

  return (
    <>
      {boxes.filter((b) => b.rect && b.rect.width > 0).map((b) => {
        const r = b.rect as DOMRect;
        return (
          <div key={b.id} data-fb-highlight={b.id} style={{ position: 'fixed', left: r.left - 4, top: r.top - 4, width: r.width + 8, height: r.height + 8,
            border: `3px solid ${COLOR}`, borderRadius: 8, background: 'rgba(249,115,22,0.08)', boxShadow: '0 0 0 4px rgba(249,115,22,0.18)',
            pointerEvents: 'none', zIndex: Z }}>
            <span style={{ position: 'absolute', left: -12, top: -12, width: 24, height: 24, borderRadius: '50%', background: COLOR, color: '#fff',
              fontSize: 13, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,.25)' }}>
              {b.idx}
            </span>
            {b.note && (
              <span style={{ position: 'absolute', left: 0, top: '100%', marginTop: 6, maxWidth: 320, padding: '6px 10px', borderRadius: 6,
                background: '#111827', color: '#fff', fontSize: 12, lineHeight: 1.5, whiteSpace: 'normal', boxShadow: '0 2px 8px rgba(0,0,0,.25)' }}>
                {b.idx}. {b.note}
              </span>
            )}
          </div>
        );
      })}

      <div role="dialog" aria-label="피드백 위치 표시" style={{ position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: Z + 1,
        width: 'min(720px, calc(100vw - 24px))', background: '#fff', border: `2px solid ${COLOR}`, borderRadius: 12,
        boxShadow: '0 6px 20px rgba(0,0,0,.18)', padding: '10px 14px', fontSize: 13, color: '#111827' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <strong style={{ color: COLOR }}>{spec.fid ? `피드백 #${spec.fid}` : '피드백'}</strong>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{spec.title}</span>
          <button type="button" onClick={close} style={{ border: '1px solid #ddd', background: '#fff', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}>
            닫기
          </button>
        </div>
        {spec.targets.length > 0 ? (
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {boxes.map((b) => (
              <button key={b.id} type="button" onClick={() => goTo(b.id)} title={b.note ?? b.id}
                style={{ border: `1px solid ${b.rect ? COLOR : '#d1d5db'}`, background: b.rect ? '#FFF7ED' : '#F3F4F6', color: b.rect ? '#9A3412' : '#6B7280',
                  borderRadius: 999, padding: '2px 10px', fontSize: 12, cursor: b.rect ? 'pointer' : 'default' }}>
                {b.idx}. #{b.id}{b.rect ? '' : waiting ? ' (찾는 중…)' : ' (지금 화면에 없음)'}
              </button>
            ))}
          </div>
        ) : (
          <div style={{ marginTop: 6, color: '#6B7280', fontSize: 12 }}>표시할 요소가 지정되지 않은 글입니다 (화면만 열었습니다).</div>
        )}
        {!waiting && missing.length > 0 && (
          <div style={{ marginTop: 6, color: '#6B7280', fontSize: 12 }}>
            지금 화면에 없는 요소는 해당 단계로 진행하거나(예: 검사를 끝까지 응답) 접힌 영역·탭을 열면 자동으로 표시됩니다. 로그인 권한이 다르면 보이지 않을 수 있습니다.
          </div>
        )}
      </div>
    </>
  );
};

export default FeedbackHighlighter;
